import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DotLoading, Empty, InfiniteScroll } from 'antd-mobile';
import type { DrawRecord, GameType, LotterySiteSettings } from '../types/lottery';
import { GAMES } from '../types/lottery';
import { getDrawHistory, getLotterySiteSettings } from '../api/lottery';
import { formatPeriod, splitDrawBalls } from '../utils/ball';
import { readCachedSiteSettings, resolveSiteName, resolveSiteShortName, writeCachedSiteSettings } from '../utils/siteSettingsCache';
import { applySiteMeta } from '../utils/siteMeta';
import LotteryBall from '../components/LotteryBall';
import AdCarousel from '../components/AdCarousel';
import './History.css';

const PAGE_SIZE = 16;
const DEFAULT_GAME: GameType = 'amssc';

function resolveGameParam(value: null | string): GameType {
  return GAMES.some((item) => item.code === value) ? (value as GameType) : DEFAULT_GAME;
}

const History: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const game = resolveGameParam(searchParams.get('game'));
  const [records, setRecords] = useState<DrawRecord[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState<LotterySiteSettings | null>(() => readCachedSiteSettings());
  const requestRef = useRef(0);
  const loadingRef = useRef(false);
  const siteSettingsRequestRef = useRef(0);

  const loadPage = useCallback(async (targetGame: GameType, targetPage: number, append: boolean) => {
    if (loadingRef.current) {
      return;
    }

    const requestId = ++requestRef.current;
    loadingRef.current = true;
    setLoading(true);
    try {
      const nextRecords = await getDrawHistory(targetGame, targetPage, PAGE_SIZE);
      if (requestId !== requestRef.current) {
        return;
      }
      setRecords((prev) => (append ? [...prev, ...nextRecords] : nextRecords));
      setPage(targetPage + 1);
      setHasMore(nextRecords.length >= PAGE_SIZE);
    } catch {
      if (requestId !== requestRef.current) {
        return;
      }
      if (!append) {
        setRecords([]);
      }
      setHasMore(false);
    } finally {
      if (requestId === requestRef.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const rawGame = searchParams.get('game');
    if (rawGame !== game) {
      setSearchParams({ game }, { replace: true });
    }
  }, [game, searchParams, setSearchParams]);

  useEffect(() => {
    requestRef.current += 1;
    loadingRef.current = false;
    setRecords([]);
    setPage(1);
    setHasMore(true);
    void loadPage(game, 1, false);
  }, [game, loadPage]);

  useEffect(() => {
    const requestId = ++siteSettingsRequestRef.current;
    getLotterySiteSettings()
      .then((res) => {
        if (requestId !== siteSettingsRequestRef.current) {
          return;
        }
        setSiteSettings(writeCachedSiteSettings(res));
      })
      .catch(() => {
        if (requestId !== siteSettingsRequestRef.current) {
          return;
        }
      });
  }, []);

  const loadMore = useCallback(async () => {
    await loadPage(game, page, page > 1);
  }, [game, loadPage, page]);

  const currentGame = GAMES.find((item) => item.code === game) || GAMES[0];
  const siteName = resolveSiteName(siteSettings) || '彩票网站';
  const siteShortName = resolveSiteShortName(siteSettings) || siteName;
  const siteNotice = siteSettings?.site_notice?.trim() || '';

  useEffect(() => {
    applySiteMeta(siteSettings, {
      title: `${siteName} - ${currentGame.name}历史`,
      appName: siteName,
      appShortName: siteShortName,
      fallbackDescription: `${siteName} ${currentGame.name} 历史开奖记录，按期号倒序展示最近开奖走势。`,
      fallbackKeywords: `${siteName},${siteShortName},${currentGame.name},历史开奖记录,走势,彩票`,
    });
  }, [currentGame.name, siteName, siteSettings, siteShortName]);

  return (
    <div className="history2-page">
      <div className="history2-topbar">
        <button type="button" className="history2-nav" onClick={() => navigate(`/lottery?game=${game}`)}>
          返回首页
        </button>
        <div className="history2-title">{siteShortName} · {currentGame.name}</div>
        <button type="button" className="history2-nav" onClick={() => void loadPage(game, 1, false)}>
          刷新
        </button>
      </div>

      <div className="history2-game-strip">
        {GAMES.map((item) => (
          <button
            key={item.code}
            type="button"
            className={`history2-game-item ${item.code === game ? 'is-active' : ''}`}
            onClick={() => setSearchParams({ game: item.code }, { replace: true })}
          >
            {item.shortName}
          </button>
        ))}
      </div>

      <div className="history2-banner">
        <div className="history2-banner-kicker">{siteName}</div>
        <div className="history2-banner-title">近期开奖走势</div>
        <div className="history2-banner-subtitle">{siteNotice || '按期号倒序展示，保留特码与正码分隔样式'}</div>
      </div>

      <AdCarousel position="history_top" />

      <div className="history2-list">
        {loading && records.length === 0 ? (
          <div className="history2-loading">
            <DotLoading />
          </div>
        ) : null}

        {!loading && records.length === 0 && !hasMore ? (
          <div className="history2-empty">
            <Empty description="暂无开奖记录" />
          </div>
        ) : null}

        {records.map((record, index) => {
          const { normalBalls, specialBalls } = splitDrawBalls(
            record.format,
            record.regular_count,
            record.special_count,
          );

          return (
            <article key={`${record.period}-${index}`} className="history2-card">
              <div className="history2-card-head">
                <div className="history2-period">{formatPeriod(record.period)}</div>
                <div className="history2-date">{record.date || '已同步'}</div>
              </div>

              <div className="history2-balls">
                {normalBalls.map((ball, ballIndex) => (
                  <LotteryBall key={`${record.period}-normal-${ballIndex}`} ball={ball} size="sm" showSx={false} />
                ))}
                {specialBalls.map((ball, ballIndex) => (
                  <LotteryBall
                    key={`${record.period}-special-${ballIndex}`}
                    ball={ball}
                    size="sm"
                    showSx={false}
                    isSpecial
                  />
                ))}
              </div>

              <div className="history2-tags">
                {record.format.map((ball, ballIndex) => (
                  <span key={`${record.period}-tag-${ballIndex}`} className="history2-tag">
                    {ball.sx?.name || '--'}
                  </span>
                ))}
              </div>
            </article>
          );
        })}

        <InfiniteScroll loadMore={loadMore} hasMore={hasMore}>
          {hasMore ? <DotLoading /> : records.length > 0 ? '没有更多了' : ''}
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default History;
